const ensureKamokMobileCartScrollStyles = () => {
  if (document.getElementById('KamOkMobileCartScrollStylesV2')) return;

  const style = document.createElement('style');
  style.id = 'KamOkMobileCartScrollStylesV2';
  style.textContent = `
    @media screen and (max-width: 749px) {
      cart-drawer,
      cart-drawer.drawer,
      .drawer {
        z-index: 12000 !important;
      }

      cart-drawer:not(.is-empty) .drawer__inner {
        height: 100dvh !important;
        max-height: 100dvh !important;
        min-height: 0 !important;
        overflow-x: hidden !important;
        overflow-y: auto !important;
        overscroll-behavior-y: contain;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-y !important;
      }

      cart-drawer:not(.is-empty) .drawer__header,
      cart-drawer:not(.is-empty) .drawer__footer {
        flex: 0 0 auto !important;
      }

      cart-drawer:not(.is-empty) cart-drawer-items {
        display: block !important;
        flex: 0 0 auto !important;
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
        overflow: visible !important;
        touch-action: pan-y !important;
      }

      cart-drawer:not(.is-empty) .cart-drawer__form,
      cart-drawer:not(.is-empty) .drawer__contents,
      cart-drawer:not(.is-empty) .drawer__cart-items-wrapper {
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
      }

      cart-drawer .cart-drawer__overlay {
        z-index: 0 !important;
      }

      cart-drawer .drawer__inner {
        position: relative;
        z-index: 1;
      }

      .kamok-overlay:not(.is-open),
      .kamok-drawer:not(.is-open) {
        pointer-events: none !important;
        visibility: hidden !important;
      }

      .kamok-overlay.is-open,
      .kamok-drawer.is-open {
        pointer-events: auto !important;
        visibility: visible !important;
      }
    }
  `;

  document.head.appendChild(style);
};

const resetKamokMobileMenuBeforeCart = () => {
  document.getElementById('kamokDrawer')?.classList.remove('is-open');
  document.getElementById('kamokOverlay')?.classList.remove('is-open');

  const hamburger = document.getElementById('kamokHamburger');
  hamburger?.classList.remove('is-open');
  hamburger?.setAttribute('aria-expanded', 'false');

  const menuDrawer = document.getElementById('kamokDrawer');
  menuDrawer?.setAttribute('aria-hidden', 'true');

  document.body.style.removeProperty('overflow');
  document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    ensureKamokMobileCartScrollStyles();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    resetKamokMobileMenuBeforeCart();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
